import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ClipboardDocumentIcon,
  ArrowTopRightOnSquareIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { useWalletStore } from '../store/walletStore';
import { getTokenInfo, getTokenReputation, getPresaleInfo } from '../api/vexforge';

const gradeColors = {
  A: 'bg-green-500/20 text-green-400 border-green-500/30',
  B: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  C: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  D: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  F: 'bg-red-500/20 text-red-400 border-red-500/30',
};

export default function TokenDetail() {
  const { mint } = useParams();
  const { address: walletAddress } = useWalletStore();
  const [token, setToken] = useState(null);
  const [reputation, setReputation] = useState(null);
  const [presale, setPresale] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const [tokenResult, repResult, presaleResult] = await Promise.allSettled([
          getTokenInfo(mint),
          getTokenReputation(mint),
          getPresaleInfo(mint),
        ]);

        if (tokenResult.status === 'fulfilled') setToken(tokenResult.value);
        if (repResult.status === 'fulfilled') setReputation(repResult.value);
        if (presaleResult.status === 'fulfilled' && presaleResult.value) setPresale(presaleResult.value);
      } catch (err) {
        console.error('Failed to fetch token:', err);
      } finally {
        setLoading(false);
      }
    }
    if (mint) fetchData();
  }, [mint]);

  const copyAddress = () => {
    navigator.clipboard.writeText(mint);
    toast.success('Address copied');
  };

  const isCreator = walletAddress && token?.creator &&
    walletAddress.toLowerCase() === token.creator.toLowerCase();

  if (loading) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-400">Loading token details...</p>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="text-center py-16">
        <h1 className="text-2xl font-bold text-white mb-2">Token Not Found</h1>
        <p className="text-gray-400">The token at {mint} could not be loaded.</p>
        <Link to="/explorer" className="text-vextaris-blue hover:text-white mt-4 inline-block">
          Back to Explorer
        </Link>
      </div>
    );
  }

  const grade = reputation?.grade || '--';
  const gradeClass = gradeColors[grade] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-4">
            {token.image ? (
              <img src={token.image} alt={token.name} className="w-16 h-16 rounded-xl object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-xl bg-gradient-vextaris flex items-center justify-center text-2xl font-bold text-white">
                {(token.symbol || '?')[0]}
              </div>
            )}
            <div>
              <div className="flex items-center space-x-2">
                <h1 className="text-2xl font-bold text-white">{token.name || 'Unknown Token'}</h1>
                {token.is_mutable === false ? (
                  <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                    <LockClosedIcon className="w-3 h-3" />
                    <span>Locked</span>
                  </span>
                ) : token.is_mutable === true ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
                    Mutable
                  </span>
                ) : null}
              </div>
              <p className="text-vextaris-blue font-mono text-sm">{token.symbol || '--'}</p>
            </div>
          </div>

          {reputation && (
            <div className={`px-4 py-2 rounded-xl border text-center ${gradeClass}`}>
              <p className="text-2xl font-bold">{grade}</p>
              <p className="text-xs opacity-80">{reputation.score}/100</p>
            </div>
          )}
        </div>

        <div className="mt-4 flex items-center space-x-2">
          <span className="text-xs text-gray-400">Mint:</span>
          <span className="text-xs text-gray-300 font-mono">{mint}</span>
          <button onClick={copyAddress} className="text-gray-400 hover:text-white">
            <ClipboardDocumentIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-vextaris-dark/50 backdrop-blur-xl rounded-xl border border-vextaris-purple/20 p-4">
          <p className="text-xs text-gray-400">Total Supply</p>
          <p className="text-lg font-semibold text-white">
            {token.total_supply ? Number(token.total_supply).toLocaleString() : '--'}
          </p>
        </div>
        <div className="bg-vextaris-dark/50 backdrop-blur-xl rounded-xl border border-vextaris-purple/20 p-4">
          <p className="text-xs text-gray-400">Decimals</p>
          <p className="text-lg font-semibold text-white">{token.decimals ?? '--'}</p>
        </div>
        <div className="bg-vextaris-dark/50 backdrop-blur-xl rounded-xl border border-vextaris-purple/20 p-4">
          <p className="text-xs text-gray-400">Mintable</p>
          <p className={`text-lg font-semibold ${token.features?.mintable ? 'text-yellow-400' : 'text-green-400'}`}>
            {token.features?.mintable ? 'Yes' : 'No'}
          </p>
        </div>
        <div className="bg-vextaris-dark/50 backdrop-blur-xl rounded-xl border border-vextaris-purple/20 p-4">
          <p className="text-xs text-gray-400">Created</p>
          <p className="text-lg font-semibold text-white">
            {token.created_at ? new Date(token.created_at * 1000).toLocaleDateString() : '--'}
          </p>
        </div>
      </div>

      {/* Reputation Breakdown */}
      {reputation?.factors && (
        <div className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 p-6">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center space-x-2">
            <ShieldCheckIcon className="w-5 h-5 text-vextaris-lime" />
            <span>Reputation Breakdown</span>
          </h2>

          <div className="space-y-3">
            {Object.entries(reputation.factors).map(([key, factor]) => (
              <div key={key}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-300 capitalize">{key.replace(/_/g, ' ')}</span>
                  <span className="text-white font-medium">{factor.points}/{factor.max_points}</span>
                </div>
                <div className="w-full h-2 bg-vextaris-darker rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-vextaris rounded-full transition-all"
                    style={{ width: `${(factor.points / factor.max_points) * 100}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{factor.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Description & Links */}
      {(token.description || token.external_url || token.twitter || token.discord || token.telegram || token.github) && (
        <div className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 p-6">
          <h2 className="text-xl font-semibold text-white mb-4">About</h2>
          {token.description && <p className="text-gray-300 mb-4">{token.description}</p>}
          <div className="flex flex-wrap items-center gap-3">
            {token.external_url && (
              <a href={token.external_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-vextaris-darker/80 border border-vextaris-purple/20 rounded-lg text-gray-300 hover:text-white hover:border-vextaris-purple/50 transition-colors text-sm">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9" /></svg>
                <span>Website</span>
              </a>
            )}
            {token.twitter && (
              <a href={token.twitter.startsWith('http') ? token.twitter : `https://x.com/${token.twitter.replace('@','')}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-vextaris-darker/80 border border-vextaris-purple/20 rounded-lg text-gray-300 hover:text-white hover:border-vextaris-purple/50 transition-colors text-sm">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                <span>Twitter</span>
              </a>
            )}
            {token.discord && (
              <a href={token.discord.startsWith('http') ? token.discord : `https://discord.gg/${token.discord}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-vextaris-darker/80 border border-vextaris-purple/20 rounded-lg text-gray-300 hover:text-white hover:border-vextaris-purple/50 transition-colors text-sm">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03z"/></svg>
                <span>Discord</span>
              </a>
            )}
            {token.telegram && (
              <a href={token.telegram.startsWith('http') ? token.telegram : `https://t.me/${token.telegram}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-vextaris-darker/80 border border-vextaris-purple/20 rounded-lg text-gray-300 hover:text-white hover:border-vextaris-purple/50 transition-colors text-sm">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                <span>Telegram</span>
              </a>
            )}
            {token.github && (
              <a href={token.github.startsWith('http') ? token.github : `https://github.com/${token.github}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-vextaris-darker/80 border border-vextaris-purple/20 rounded-lg text-gray-300 hover:text-white hover:border-vextaris-purple/50 transition-colors text-sm">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>
                <span>GitHub</span>
              </a>
            )}
          </div>
        </div>
      )}

      {/* Creator */}
      <div className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 p-6">
        <h2 className="text-xl font-semibold text-white mb-2">Creator</h2>
        <p className="text-gray-300 font-mono text-sm">{token.creator || '--'}</p>
        {isCreator && token.is_mutable !== false && (
          <Link
            to={`/token/${mint}/edit`}
            className="mt-4 inline-flex items-center space-x-2 px-4 py-2 bg-vextaris-purple/10 border border-vextaris-purple/30 rounded-lg text-vextaris-purple hover:bg-vextaris-purple/20 transition-colors text-sm"
          >
            <PencilSquareIcon className="w-4 h-4" />
            <span>Update Metadata</span>
          </Link>
        )}
        {isCreator && token.is_mutable === false && (
          <div className="mt-4 inline-flex items-center space-x-2 px-4 py-2 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-sm">
            <LockClosedIcon className="w-4 h-4" />
            <span>Metadata is permanently locked</span>
          </div>
        )}
      </div>

      {/* Presale Card */}
      {presale && (
        <div className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white">Presale</h2>
            <span className={`px-3 py-1 rounded-full text-xs font-bold ${
              presale.status === 'Active' ? 'bg-vextaris-lime/20 text-vextaris-lime' :
              presale.status === 'Finalized' ? 'bg-green-500/20 text-green-400' :
              presale.status === 'Cancelled' ? 'bg-red-500/20 text-red-400' :
              'bg-yellow-500/20 text-yellow-400'
            }`}>
              {presale.status}
            </span>
          </div>

          {/* Progress */}
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-gray-400">Raised</span>
              <span className="text-white">{presale.total_raised || 0} / {presale.hard_cap || '--'} VXS</span>
            </div>
            <div className="w-full h-3 bg-vextaris-darker rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-vextaris rounded-full"
                style={{ width: `${presale.hard_cap ? Math.min((presale.total_raised / presale.hard_cap) * 100, 100) : 0}%` }}
              />
            </div>
          </div>

          <Link
            to={`/presale/${mint}`}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-vextaris text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
          >
            <span>View Presale</span>
          </Link>
        </div>
      )}
    </div>
  );
}